import { zodResolver } from "@hookform/resolvers/zod";
import type { CompositeJob, MechanicalJob } from "@stryker/core/types/jobs";
import type { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import Copy2Icon from "@/assets/icons/copy-2.svg?react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { getJobTypeRoute } from "@/utils/navigation";

type DuplicateJobModalProps = {
  jobIdToDuplicate: number | null;
  setJobIdToDuplicate: Dispatch<SetStateAction<null | number>>;
  job: CompositeJob | MechanicalJob | null;
};
const duplicateJobScheme = z.object({
  name: z.string().nonempty("Job name is required"),
});

type DuplicateJobScheme = z.infer<typeof duplicateJobScheme>;

export default function DuplicateJobModal({ jobIdToDuplicate, setJobIdToDuplicate, job }: DuplicateJobModalProps) {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DuplicateJobScheme>({
    resolver: zodResolver(duplicateJobScheme),
  });

  const onDuplicateJob = (data: DuplicateJobScheme) => {
    const jobTypeRoute = job?.job_type ? getJobTypeRoute(job.job_type) : "composite";
    navigate(`/jobs/create/${jobTypeRoute}?jobId=${jobIdToDuplicate}&jobName=${encodeURIComponent(data.name)}`);
  };

  return (
    <Dialog open={!!jobIdToDuplicate} onOpenChange={() => setJobIdToDuplicate(null)}>
      <DialogTitle className="hidden">Duplicate Job {jobIdToDuplicate}</DialogTitle>
      <DialogDescription className="hidden">
        Create a copy of this job with a new plant repair identifier
      </DialogDescription>
      <DialogContent>
        <form className="min-w-[300px]" onSubmit={handleSubmit(onDuplicateJob)}>
          <div className="mb-4 flex justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
              <Copy2Icon className="stroke-blue-500" />
            </div>
          </div>
          <Typography variant="text-lg" weight="semibold" className="text-gray-900">
            Duplicate Job
          </Typography>
          <Typography variant="text-small" className="mb-4 text-gray-600">
            When duplicating job, you should provide a Plant Repair Identifier
          </Typography>
          <TextInput
            autoComplete="off"
            label="Plant Repair Identifier"
            placeholder="Plant Repair Identifier"
            error={errors.name?.message}
            {...register("name")}
          />
          <div className="mt-14 flex justify-between gap-4">
            <Button
              type="button"
              size="lg"
              variant="outline"
              className="flex-1"
              onClick={() => setJobIdToDuplicate(null)}
            >
              Cancel
            </Button>
            <Button type="submit" size="lg" className="flex-1">
              Submit
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
